/**
 * Shared motion presets — the single source of truth for animation timing so
 * the app's motion language stays consistent (and is tunable in one place).
 *
 * Curves:
 *   EASE_PREMIUM     — out-expo: fast start, gentle settle. Entrances/reveals.
 *   EASE_PREMIUM_IN  — in-expo: gentle start, decisive finish. Exits.
 *
 * Durations are deliberately on the slower side of "snappy" so reveals feel
 * graceful rather than abrupt. Exits stay quicker than entrances — leaving
 * should feel decisive, not lingering.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * KEEP IN SYNC WITH src/index.css — the `@theme` block there mirrors these
 * curves (`--ease-premium` / `--ease-premium-in`) and the durations below
 * (`--duration-*`). JS-driven motion (motion/react) reads from here; CSS- and
 * Tailwind-driven motion reads the CSS vars. Change a value in one place and
 * update its twin in the other so the two motion systems never drift.
 * ──────────────────────────────────────────────────────────────────────────
 */
export const EASE_PREMIUM = [0.16, 1, 0.3, 1] as const;
export const EASE_PREMIUM_IN = [0.7, 0, 0.84, 0] as const;

/**
 * Symmetric ease-in-out for *standalone opacity-only* crossfades — a fade with
 * nothing else moving and nothing else animating alongside it. Out-expo
 * (EASE_PREMIUM) front-loads opacity, so an isolated pure fade with it reads as
 * a sudden "appear". NOTE: the route-level page fade no longer uses this — it
 * shares EASE_PREMIUM with the section Reveals so the whole entrance reads as
 * one bloom (the reveals' rise masks the out-expo opacity). Reach for this only
 * when a fade truly stands alone.
 */
export const EASE_FADE = [0.42, 0, 0.58, 1] as const;

/**
 * Canonical entrance-rise distance (px). Reveals fade in *and* drift up by
 * this much — the app-wide "enter" feel. Reduced-motion drops the rise and
 * fades only. Kept small so movement stays a subtle settle, never a slide.
 */
export const RISE_Y = 12;

export const MOTION = {
  /**
   * Reveal/entrance — deliberate but not laboring. Pair with `stagger`
   * (0.06) below for a cascade across 7 cards of ~780ms total
   * (7 × 0.06 + 0.36 ≈ 780ms). Opacity-only (no y-translate) so it stays
   * butter-smooth — physics-aware easing handles the rest.
   */
  enter: 0.36,
  /** Per-item cascade step for staggered siblings (caller caps the index). */
  stagger: 0.06,
  /**
   * Base delay before a page's inner load-in cascade (card grid, detail/
   * onboarding Reveals) begins, relative to the route fade. Set to exactly one
   * `stagger` step so the whole entrance reads as ONE metronomic bloom — the
   * page surface is beat 0, the first section beat 1 (this delay), the next
   * beat 2, and so on, evenly spaced. (It used to be a larger 0.18 gap that
   * sequenced the cascade *behind* the fade as a separate beat; that read as
   * two disconnected motions, so the cascade now blooms together with the
   * fade on the same curve.)
   */
  stageDelay: 0.06,
  /** Exit — snappier than entrance, but not jarring. */
  exit: 0.24,
  /** Layout reflow when items reposition (add/remove/filter). */
  layout: 0.32,
  /** Page-level route transition. */
  pageEnter: 0.5,
  pageExit: 0.22,
  /**
   * Section/element reveal — the staggered fade-and-rise used by the shared
   * `<Reveal>`. Slightly longer than a card `enter` so larger blocks settle
   * gracefully rather than snapping.
   */
  reveal: 0.5,
  /** Crossfade between list states (loading ↔ empty ↔ cards). */
  crossfade: 0.22,
  /** Long progress-track fill (the multi-tone campaign timeline). */
  track: 0.9,
  /** Short bar/meter fill (e.g. the reach meter). */
  meter: 0.5,
  /** Radar-ping pulse cycle (active-node halo, "working" icon). */
  pulse: 1.9,
  /** Quiet gap between successive radar pings. */
  pulseGap: 0.4,
  /**
   * Micro-interaction duration (s) for hover/color/press transitions. Mirrors
   * the CSS `--default-transition-duration` (200ms) so JS- and Tailwind-driven
   * micro-transitions share one tempo. Tailwind side standardizes on the
   * `duration-200 ease-premium` pairing (now the global default — see
   * index.css Lever A).
   */
  micro: 0.2,
  /** Minimal durations used when the user prefers reduced motion. */
  reducedEnter: 0.15,
  reducedPage: 0.1,
} as const;
